import type { ReactNode } from "react";
import type {
  ApiIdentity,
  ApiIdentityPlugin,
  ZudokuConfig,
  ZudokuContext,
} from "zudoku";
import { Callout } from "zudoku/components";
import { blogPlugin } from "zudoku/plugins/blog";
import { generateWebhookCodeSnippet } from "./src/CodeSnippetGenerator";
import { Landingpage } from "./src/Landingpage";
import { MembersOnly } from "./src/MembersOnly";
import { NotFound } from "./src/NotFound";
import { VipLounge } from "./src/VipLounge";
import "./custom.css";

// The Employee MCP Servers API documents internal tooling, so it is hidden from
// the catalog and its routes are blocked unless the crew member is signed in.
// It also lives outside the `/catalog` path so the catalog nav item isn't
// highlighted at the same time as the Employee MCP one.
const EMPLOYEE_MCP_PATH = "/employee-mcp";

// The REST side of the same registry. It lives in its own schema because
// `x-zudoku-type: mcp-catalog` renders only the MCP servers in a document, so
// plain endpoints kept alongside them would never be shown.
const GATEWAY_DIRECTORY_PATH = "/catalog/api-gateway-directory";

// Clerk publishable keys are shipped to the browser by design, so both are safe
// to commit. The production instance is served from clerk.cosmocargo.dev and is
// scoped to that domain, so it only authenticates on the production deployment;
// preview deployments and local development have to use the development
// instance instead.
const CLERK_PUB_KEY_PRODUCTION = "pk_live_Y2xlcmsuY29zbW9jYXJnby5kZXYk";
const CLERK_PUB_KEY_DEVELOPMENT =
  "pk_test_dG9sZXJhbnQtaG9ybmV0LTQ2LmNsZXJrLmFjY291bnRzLmRldiQ";

// `ZUDOKU_PUBLIC_VERCEL_ENV` mirrors Vercel's `VERCEL_ENV` and is set by the
// build command in vercel.json. Only ZUDOKU_PUBLIC_/ZUPLO_PUBLIC_ variables are
// inlined into the client bundle, so reading `VERCEL_ENV` here would resolve to
// `undefined` in the browser and always pick the production key.
const CLERK_PUB_KEY = (process.env.ZUDOKU_PUBLIC_CLERK_PUB_KEY ??
  (process.env.ZUDOKU_PUBLIC_VERCEL_ENV === "production"
    ? CLERK_PUB_KEY_PRODUCTION
    : CLERK_PUB_KEY_DEVELOPMENT)) as `pk_test_${string}` | `pk_live_${string}`;

export class CosmoCargoApiIdentityPlugin implements ApiIdentityPlugin {
  async getIdentities(context: ZudokuContext) {
    if (!context.getAuthState().isAuthenticated) {
      return [];
    }

    return [
      {
        label: `Unlimited Subscription`,
        id: "UNLNTD",
        authorizeRequest: async (request: Request) => {
          request.headers.set("Authorization", `Bearer 123123`);

          return request;
        },
        authorizationFields: {
          headers: ["Authorization"],
        },
      },
    ] satisfies ApiIdentity[];
  }
}

const config: ZudokuConfig = {
  metadata: {
    title: "%s | Cosmo Cargo Inc.",
    defaultTitle: "Cosmo Cargo Inc.",
    description: "Interstellar shipping API documentation",
  },
  header: {
    navigation: [
      {
        label: "Solutions",
        items: [
          {
            label: "Shipment Tracking",
            icon: "satellite",
            description: "Real-time quantum-entangled tracking across galaxies",
            to: "/documentation",
          },
          {
            label: "Warp Logistics",
            icon: "zap",
            description: "FTL delivery for time-critical cargo",
            to: "/api-shipments/shipment-management",
          },
          {
            label: "API Catalog",
            icon: "layout-grid",
            description: "Browse all available Cosmo Cargo APIs",
            to: "/catalog",
          },
        ],
      },
      {
        label: "Products",
        items: [
          {
            label: "Shipping",
            items: [
              {
                label: "Interstellar Express",
                icon: "rocket",
                description: "Cross-system delivery in under 12 parsecs",
                to: "/documentation",
              },
              {
                label: "Quantum Freight",
                icon: "atom",
                description: "Bulk cargo via quantum tunneling",
                to: "/documentation",
              },
            ],
          },
          {
            label: "Developer Tools",
            items: [
              {
                label: "Shipment API",
                icon: "code",
                description: "Integrate shipping into your apps",
                to: "/api-shipments/shipment-management",
              },
              {
                label: "Webhooks",
                icon: "webhook",
                description: "Event-driven cargo notifications",
                to: "/catalog",
              },
            ],
          },
        ],
      },
    ],
    placements: {
      // search: "start",
      navigation: "end",
      auth: "end",
    },
  },
  docs: {
    publishMarkdown: true,
    contentNegotiation: true,
    llms: {
      llmsTxt: true,
      llmsTxtFull: true,
      title: "Cosmo Cargo Developer Platform",
      description:
        "Build interstellar shipping, tracking, fleet, and cargo automation with Cosmo Cargo APIs.",
      instructions:
        "Use these docs when planning, booking, tracking, or automating interstellar cargo shipments. Start with the documentation guide for platform concepts, then use the published Shipment API specification at /openapi.json for callable operations and typed request and response schemas.",
    },
  },
  sitemap: {
    siteUrl: "https://cosmocargo.dev",
  },
  site: {
    sidebar: {
      togglePosition: "center",
      toggleVisibility: "hover",
    },
    notFoundPage: <NotFound />,
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      width: 165,
      height: 24,
      alt: "Cosmo Cargo Inc.",
    },
    banner: {
      message: (
        <div className="text-center">
          We're announcing 🧑‍🚀 inter-galactic shipping ✨ for 3025!
        </div>
      ),
      dismissible: true,
    },
    footer: {
      columns: [
        {
          title: "Product",
          links: [
            { label: "Features", href: "https://zudoku.dev" },
            {
              label: "Docs",
              href: "https://zudoku.dev/docs/?utm_source=cosmo-cargo",
            },
          ],
        },
        {
          title: "Company",
          links: [
            { label: "About", href: "https://zuplo.com/about" },
            { label: "Blog", href: "/blog" },
            { label: "Careers", href: "https://zuplo.com/careers" },
          ],
        },
        {
          title: "Resources",
          links: [
            { label: "API Reference", href: "/" },
            { label: "Status", href: "/" },
            { label: "Support", href: "/" },
          ],
        },
        {
          title: "Legal",
          links: [
            { label: "Privacy", href: "/" },
            { label: "Terms", href: "/" },
            { label: "Security", href: "/" },
          ],
        },
      ],
      social: [
        {
          href: "https://github.com/zuplo/zudoku",
          icon: "github",
        },
        {
          href: "https://twitter.com/zuplo",
          icon: "x",
        },
        {
          href: "https://discord.zudoku.dev",
          icon: "discord",
        },
      ],
      copyright: `© ${new Date().getFullYear()} Zuplo, Inc. All rights reserved.`,
      logo: {
        src: {
          light: "https://cdn.zudoku.dev/logos/zudoku-logo-full-light.svg",
          dark: "https://cdn.zudoku.dev/logos/zudoku-logo-full-dark.svg",
        },
        alt: "Zudoku by Zuplo",
        width: 120,
        height: 24,
      },
    },
  },
  plugins: [
    new CosmoCargoApiIdentityPlugin(),
    blogPlugin({
      title: "Cosmo Cargo Dispatch",
      description:
        "Launches, lane reports, and field notes from the Cosmo Cargo crew.",
      postsPerPage: 3,
      authors: {
        nova: {
          name: "Nova Reyes",
          title: "Head of Product, Cosmo Cargo",
          url: "https://cosmocargo.dev",
        },
        orion: {
          name: "Orion Vance",
          title: "Fleet Operations Lead",
        },
      },
    }),
    {
      getHead: () => (
        <>
          <meta name="cosmo-cargo-head-test" content="verified" />
          <script>{`window.__COSMO_HEAD_TEST=true`}</script>
        </>
      ),
    },
  ],
  protectedRoutes: {
    [`${EMPLOYEE_MCP_PATH}/*`]: ({ auth, reasonCode }) =>
      auth.isAuthenticated ? true : reasonCode.UNAUTHORIZED,
    [`${GATEWAY_DIRECTORY_PATH}/*`]: ({ auth, reasonCode }) =>
      auth.isAuthenticated ? true : reasonCode.UNAUTHORIZED,
    "/only-members": ({ auth, reasonCode }) =>
      auth.isAuthenticated ? true : reasonCode.UNAUTHORIZED,
    "/vip-lounge": ({ auth, reasonCode }) =>
      !auth.isAuthenticated
        ? reasonCode.UNAUTHORIZED
        : auth.profile?.email?.endsWith("@zuplo.com")
          ? true
          : reasonCode.FORBIDDEN,
  },
  mdx: {
    components: {
      Trademark: () => "™",
      SpaceWarning: ({
        children,
        sector,
      }: {
        children: ReactNode;
        sector?: string;
      }) => (
        <Callout
          type="danger"
          title={sector ? `Sector ${sector} Advisory` : "Space Advisory"}
        >
          {children}
        </Callout>
      ),
    },
  },
  navigationRules: [
    {
      type: "modify",
      match: "Shipments/1",
      set: { icon: "ship" },
    },
    {
      type: "modify",
      match: "Shipments/2",
      set: { icon: "box" },
    },
    {
      type: "insert",
      match: "Shipments/-1",
      position: "after",
      items: [
        {
          type: "link",
          label: "System Status",
          to: "/status",
          icon: "satellite",
        },
      ],
    },
  ],
  navigation: [
    {
      type: "custom-page",
      path: "/",
      element: <Landingpage />,
    },
    {
      type: "category",
      label: "Documentation",
      icon: "book-open",
      items: [
        { type: "filter", placeholder: "Filter documentation" },
        "documentation",
        {
          type: "category",
          icon: "telescope",
          collapsed: false,
          label: "Space Operations",
          items: [
            "shipping-process",
            "warp-lane-tutorial",
            "cargo-manifest-guide",
            "tracking",
            "quantum-express",
            "ship-states",
          ],
        },
        {
          type: "category",
          icon: "library-big",
          label: "Shipping Guides",
          items: ["global", "interstellar", "intergalactic"],
        },
        {
          type: "category",
          icon: "book-marked",
          label: "Cargo Handbook",
          stack: true,
          items: [
            "cargo-handbook/hazardous",
            "cargo-handbook/cryo",
            "cargo-handbook/living",
            "cargo-handbook/anomalous",
          ],
        },
        {
          type: "category",
          icon: "rocket",
          label: "Fleet Management",
          items: [
            "fleet/warp-drives",
            "fleet/maintenance",
            "fleet/crew",
            "fleet/refueling",
          ],
        },
        {
          type: "category",
          icon: "credit-card",
          label: "Billing & Credits",
          items: [
            "billing/invoices",
            "billing/galactic-credits",
            "billing/refunds",
          ],
        },
        {
          type: "category",
          icon: "shield-check",
          label: "Compliance",
          items: [
            "compliance/customs",
            "compliance/quarantine",
            "compliance/treaties",
          ],
        },
        {
          type: "category",
          icon: "life-buoy",
          label: "Support",
          items: ["support/contact", "support/faq", "support/escalations"],
        },
        { type: "separator" },
        {
          type: "link",
          label: "See Shipment API",
          to: "/api-shipments",
          icon: "arrow-right",
        },
        {
          type: "category",
          icon: "shield",
          label: "Premium Guides",
          display: "auth",
          items: [
            {
              type: "doc",
              file: "member-benefits",
              label: "Member Benefits",
              icon: "user-plus",
            },
            {
              type: "doc",
              file: "premium-fleet",
              path: "premium-fleet-services",
              label: "Premium Fleet Services",
              icon: "trophy",
            },
          ],
        },
      ],
    },
    {
      type: "link",
      icon: "ship",
      to: "/api-shipments",
      label: "Shipments",
    },
    {
      type: "link",
      icon: "square-library",
      to: "/catalog",
      label: "API Catalog",
    },
    {
      type: "link",
      icon: "newspaper",
      to: "/blog",
      label: "Blog",
    },
    {
      type: "link",
      icon: "bot",
      to: EMPLOYEE_MCP_PATH,
      label: "Employee MCP",
      display: "auth",
    },
    {
      type: "custom-page",
      path: "/only-members",
      label: "Only members",
      display: "auth",
      element: <MembersOnly />,
    },
    {
      type: "custom-page",
      path: "/vip-lounge",
      label: "VIP Lounge",
      display: "auth",
      element: <VipLounge />,
    },
  ],
  redirects: [
    {
      from: "/api-shipments/create-shipment",
      to: "/api-shipments/shipment-management#post-shipments",
    },
    {
      from: "/api-shipments/get-rates",
      to: "/api-shipments/rates-and-billing#post-shipments-shipmentid-rates",
    },
    {
      from: "/api-shipments/track-shipment",
      to: "/api-shipments/tracking-and-notifications#get-shipments-shipmentid-events",
    },
    // Everything under the legacy /docs prefix, e.g. /docs/test →
    // /documentation/test (and /docs itself → /documentation)
    { from: "/docs/*", to: "/documentation/*" },
    // The old starship manual chapters now live under /fleet, e.g.
    // /starship-manual/warp-drives → /fleet/warp-drives
    { from: "/starship-manual/:chapter", to: "/fleet/:chapter" },
  ],
  catalogs: {
    path: "catalog",
    label: "API Catalog",
    filterItems: (items, { auth }) =>
      auth.isAuthenticated
        ? items
        : items.filter(
            (item) =>
              item.path !== EMPLOYEE_MCP_PATH &&
              item.path !== GATEWAY_DIRECTORY_PATH,
          ),
  },
  authentication: {
    type: "clerk",
    clerkPubKey: CLERK_PUB_KEY,
    redirectToAfterSignIn: "/documentation",
    redirectToAfterSignUp: "/documentation",
  },
  defaults: {
    apis: {
      examplesLanguage: "js",
      schemaDownload: {
        enabled: true,
        fileName: "openapi",
      },
    },
  },
  search: {
    type: "pagefind",
  },
  apis: [
    {
      type: "file",
      input: "./schema/shipments.json",
      path: "api-shipments",
      publish: {
        path: "/openapi.json",
        agentQuality: true,
      },
      categories: [
        { label: "Core", tags: ["Shipments", "Logistics"] },
        { label: "Logistics", tags: ["Shipments"] },
      ],
      options: {
        disableSecurity: false,
        transformExamples: ({ content, auth }) => {
          if (!auth.isAuthenticated) {
            return content;
          }
          return content.map((c) => ({
            ...c,
            examples: c.examples?.map((e) => {
              if (e.name === "domestic" || e.name === "simple") {
                return {
                  ...e,
                  value: {
                    ...e.value,
                    recipientEmail: auth?.profile?.email,
                  },
                };
              }
              return e;
            }),
          }));
        },
      },
    },
    {
      type: "file",
      input: [
        {
          input: "./schema/label-v3.json",
          path: "latest",
          label: "Latest (3.0.0)",
        },
        "./schema/label-v2.json",
        "./schema/label-v1.json",
      ],
      path: "/catalog/api-label",
      categories: [
        { label: "Core", tags: ["Labels"] },
        { label: "Printing", tags: ["Labels"] },
      ],
    },
    {
      type: "file",
      input: "./schema/webhooks.json",
      path: "/catalog/api-webhooks",
      categories: [
        { label: "Platform", tags: ["Events", "Developer"] },
        { label: "Integrations", tags: ["Developer"] },
      ],
      options: {
        supportedLanguages: [
          { value: "js", label: "JavaScript" },
          { value: "python", label: "Python" },
          { value: "java", label: "Java" },
        ],
        generateCodeSnippet: ({
          selectedLang,
          selectedServer,
          operation,
          example,
        }) => {
          return generateWebhookCodeSnippet(
            selectedLang,
            selectedServer,
            operation.operationId,
            example,
          );
        },
      },
    },
    {
      type: "file",
      input: "./schema/interplanetary.json",
      path: "/catalog/api-interplanetary",
      categories: [{ label: "Interplanetary", tags: ["Routes", "Transit"] }],
    },
    {
      type: "file",
      input: "./schema/tracking-v1.json",
      path: "/catalog/api-tracking",
      categories: [
        { label: "Core", tags: ["Tracking", "Events"] },
        { label: "Insights", tags: ["Tracking"] },
      ],
    },
    {
      type: "file",
      input: "./schema/ai-cargo.json",
      path: "/catalog/api-ai-cargo",
      categories: [
        {
          label: "AI & Automation",
          tags: ["MCP Integration", "AI Optimization"],
        },
      ],
    },
    {
      type: "file",
      input: "./schema/docs.json",
      path: "/catalog/api-docs",
      categories: [
        {
          label: "Platform",
          tags: ["Documentation"],
        },
      ],
    },
    {
      type: "file",
      input: "./schema/employee-mcp.json",
      path: EMPLOYEE_MCP_PATH,
      categories: [
        {
          label: "AI & Automation",
          tags: ["MCP Integration", "Employee Tools"],
        },
        { label: "Internal", tags: ["Employee Tools"] },
      ],
    },
    {
      type: "file",
      input: "./schema/gateway-directory.json",
      path: GATEWAY_DIRECTORY_PATH,
      categories: [{ label: "Internal", tags: ["Employee Tools"] }],
    },
    {
      type: "file",
      input: "./schema/cargo-containers.json",
      path: "/catalog/api-cargo-containers",
      categories: [
        { label: "Core", tags: ["Containers", "Booking"] },
        { label: "Storage", tags: ["Containers"] },
      ],
    },
    {
      type: "file",
      input: [
        {
          input: "./schema/fleet-ops.json?prefix=/v3",
          path: "v3",
          label: "v3 (Quantum)",
        },
        {
          input: "./schema/fleet-ops.json?prefix=/v2",
          path: "v2",
          label: "v2 (Warp)",
        },
        {
          input: "./schema/fleet-ops.json?prefix=/v1",
          path: "v1",
          label: "v1 (Sublight)",
        },
      ],
      path: "/catalog/api-fleet-ops",
      categories: [
        { label: "Core", tags: ["Fleet Command"] },
        { label: "Operations", tags: ["Fleet Command"] },
      ],
    },
  ],
  theme: {
    light: {
      background: "oklch(0.995 0.002 80)",
      foreground: "#262626",
      card: "#fff",
      cardForeground: "#262626",
      primary: "#f4bf32",
      primaryForeground: "#0f1719",
      secondary: "60 4.8% 95.9%",
      secondaryForeground: "24 9.8% 10%",
      muted: "#f5f5f5",
      mutedForeground: "var(--color-zinc-500)",
      accent: "60 4.8% 95.9%",
      accentForeground: "24 9.8% 10%",
      destructive: "0 84.2% 60.2%",
      destructiveForeground: "60 9.1% 97.8%",
      border: "20 5.9% 90%",
      input: "20 5.9% 90%",
      ring: "oklch(0.708 0 0)",
      radius: "0.4rem",
    },
    dark: {
      background: "#1a1a18",
      foreground: "60 9.1% 97.8%",
      card: "#242424",
      cardForeground: "#f2e9e4",
      popover: "20 14.3% 4.1%",
      popoverForeground: "60 9.1% 97.8%",
      primary: "#f4bf32",
      primaryForeground: "#0f1719",
      secondary: "12 6.5% 15.1%",
      secondaryForeground: "60 9.1% 97.8%",
      muted: "#222",
      mutedForeground: "var(--color-zinc-400)",
      accent: "12 6.5% 15.1%",
      accentForeground: "60 9.1% 97.8%",
      destructive: "0 62.8% 30.6%",
      destructiveForeground: "60 9.1% 97.8%",
      border: "hsl(12deg 4.75% 24.84%)",
      input: "hsl(12deg 4.88% 29.61%)",
      ring: "35.5 91.7% 32.9%",
    },
  },
};

export default config;
